import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as queryMediumsApi from './query-mediums.api';

import type {
  CreateQueryMediumRequest,
  QueryMediumListQuery,
  UpdateQueryMediumRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: QueryMediumListQuery) => ['query-mediums', 'list', query] as const,
  detail: (id: string) => ['query-mediums', 'detail', id] as const,
};

export const useQueryMediumsList = (query: QueryMediumListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => queryMediumsApi.listQueryMediums(query),
    staleTime: 30_000,
  });

export const useCreateQueryMedium = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQueryMediumRequest) => queryMediumsApi.createQueryMedium(payload),
    onSuccess: (qm) => {
      qc.invalidateQueries({ queryKey: ['query-mediums', 'list'] });
      toast.success(`Added ${qm.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add query medium'),
  });
};

export const useUpdateQueryMedium = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateQueryMediumRequest }) =>
      queryMediumsApi.updateQueryMedium(id, payload),
    onSuccess: (qm) => {
      qc.invalidateQueries({ queryKey: ['query-mediums', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(qm.id) });
      toast.success('Query medium updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update query medium'),
  });
};

export const useDeleteQueryMedium = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => queryMediumsApi.deleteQueryMedium(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['query-mediums', 'list'] });
      toast.success('Query medium deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete query medium'),
  });
};

export const useBulkDeleteQueryMediums = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => queryMediumsApi.bulkDeleteQueryMediums(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['query-mediums', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete query mediums'),
  });
};

export const useBulkSetQueryMediumsStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      queryMediumsApi.bulkSetQueryMediumsStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['query-mediums', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update query medium status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
